import React, { Suspense } from "react";
import App from "next/app";
import Router, { withRouter, type Router as LegacyRouter } from "next/router";
import { Amplify, Auth } from "aws-amplify";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tippy.js/dist/tippy.css";
import "./../src/data/designClasses.css";
import Cookies from "js-cookie";
import * as workerTimers from "worker-timers";
import Head from "next/head";
import { DefaultSeo } from "next-seo";
import { defaultSeo } from "../src/config/next-seo.config";
import "./../public/css/App.css";
import "./../public/css/Styles.css";
import "../public/css/designClasses.css";
import "../public/css/RevampStaticPages.css";
import { setJwtToken } from "../src/crud/axios";
import awsconfig from "./../src/config/aws.config";
import RedirectChecker from "./../src/common/RedirectChecker";
import AuthChecker from "./../src/common/AuthChecker";
import AuthContext from "./../src/contexts/AuthContext";

type MyAppProps = {
  router: LegacyRouter;
  Component: any;
  pageProps: any;
  user?: any;
  signedIn?: boolean | null;
  err?: any;
};

interface MyAppState {
  user: any;
  signedIn: boolean | null;
  overlay: boolean;
  refreshToken: any;
  hasCookiesAccepted: boolean;
}

Amplify.configure({
  ...awsconfig,
  urlOpener: async (url: string) => {
    if (typeof window === "undefined") return;
    if (url.includes("linkedin") || window.location.pathname === "/linkedin-share") {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      window.location.href = url;
      return url;
    }
    return window.open(url, "_self");
  },
  ssr: true
});

class MyApp extends App<MyAppProps> {
  state: MyAppState = {
    user: this.props.user || { attributes: {} },
    signedIn: this.props.signedIn !== undefined ? this.props.signedIn : null,
    overlay: true,
    refreshToken: null,
    hasCookiesAccepted: Cookies.get("Cookie") !== undefined
  };

  private refreshTimeout: number | null = null;

  constructor(props: MyAppProps) {
    Cookies.remove("authToken");
    super(props);

    if (typeof window !== "undefined") {
      if (
        window.location.search.includes("code") &&
        window.location.pathname === "/linkedin-share"
      ) {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        if (code) {
          urlParams.set("linkedin_code", code);
          urlParams.delete("code");
          const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
          setTimeout(() => {
            window.history.replaceState(null, "", newUrl);
          }, 0);
        }
      }
    }
  }

  async componentDidMount(): Promise<void> {
    const queryParams = this.props.router.query as Record<string, any>;
    let refreshToken: any = null;

    Router.events.on("routeChangeComplete", this.authAndRedirectCheck);

    const { user, signedIn } = (await AuthChecker(null, true)) as {
      user: any;
      signedIn: boolean | null;
    };

    if (!signedIn && Cookies.get("authToken") !== undefined) {
      Cookies.remove("authToken");
      if (window.location.pathname !== "/linkedin-share-poc") {
        this.props.router.replace("/", undefined, { shallow: false });
      }
    } else if (!signedIn) {
      RedirectChecker(this.state.signedIn, {
        pathname: this.props.router.pathname,
        asPath: this.props.router.asPath
      } as any);
    } else {
      try {
        const session = await Auth.currentSession();
        setJwtToken(session.getIdToken().getJwtToken());
        await this.startTokenRefreshLoop();
        if (typeof document !== "undefined") {
          document.addEventListener("visibilitychange", this.handleVisibilityChange);
        }
      } catch (e) {
        console.error("Failed to init session on mount:", e);
        window.location.href = "/logout";
      }
    }

    setTimeout(() => {
      if (typeof window !== "undefined") {
        if (queryParams.utm_campaign) localStorage.setItem("utm_campaign", queryParams.utm_campaign);
        if (queryParams.utm_medium) localStorage.setItem("utm_medium", queryParams.utm_medium);
        if (queryParams.utm_source) localStorage.setItem("utm_source", queryParams.utm_source);
      }
    }, 0);

    this.setState({ ...this.state, user, signedIn, refreshToken });
  }

  componentWillUnmount(): void {
    Router.events.off("routeChangeComplete", this.authAndRedirectCheck);
  }

  handleVisibilityChange = async (): Promise<void> => {
    if (document.visibilityState === "visible") {
      try {
        const session = await Auth.currentSession();
        setJwtToken(session.getIdToken().getJwtToken());
        this.startTokenRefreshLoop();
      } catch (err) {
        console.error("Session refresh on visibility failed", err);
        window.location.href = "/logout";
      }
    }
  };

  startTokenRefreshLoop = async (): Promise<void> => {
    this.clearTokenRefreshLoop();
    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken();
      const expiryTime = idToken.getExpiration() * 1000;
      const now = Date.now();
      const delay = Math.max(1000, expiryTime - now - 30000);

      this.refreshTimeout = workerTimers.setTimeout(async () => {
        try {
          const newSession = await Auth.currentSession();
          setJwtToken(newSession.getIdToken().getJwtToken());
          this.startTokenRefreshLoop();
        } catch (err) {
          console.error("Token refresh failed", err);
          window.location.href = "/logout";
        }
      }, delay) as unknown as number;
    } catch (err) {
      console.error("Error scheduling token refresh", err);
    }
  };

  clearTokenRefreshLoop = (): void => {
    if (this.refreshTimeout) {
      workerTimers.clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
  };

  authAndRedirectCheck = async (): Promise<void> => {
    const authData = (await AuthChecker(null)) as { user: any; signedIn: boolean | null };
    RedirectChecker(authData.signedIn, {
      pathname: this.props.router.pathname,
      asPath: this.props.router.asPath
    } as any);
  };

  setAuth = (user: any = {}, signedIn: boolean = true): void => {
    clearInterval(this.state.refreshToken);
    this.setState({ ...this.state, user, signedIn });
  };

  hideOverlayLoading = (): void => {
    if (
      typeof document !== "undefined" &&
      document.getElementsByClassName("overlay-loader")[0] !== undefined
    ) {
      document.getElementsByClassName("overlay-loader")[0].classList.remove("d-block");
      document.getElementsByClassName("overlay-loader")[0].classList.add("d-none");
    }
  };

  acceptCookie = (): void => {
    this.setState({ ...this.state, hasCookiesAccepted: true });
  };

  render(): JSX.Element {
    const { Component, pageProps, err } = this.props;
    const { user, signedIn, hasCookiesAccepted } = this.state;
	  (pageProps as any).signedIn = signedIn;
	  
	  this.hideOverlayLoading?.();

    return (
      <>
        <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
        <DefaultSeo {...defaultSeo} />
        <Head>
          <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon.png" />
          <link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
          <link rel="icon" type="image/png" sizes="16x16" href="/favicon/favicon-32x32.png" />
          <link rel="manifest" href="/favicon/site.webmanifest" />
          <link rel="mask-icon" href="/favicon/safari-pinned-tab.svg" color="#5bbad5" />
          <link rel="preconnect" href="https://www.google-analytics.com" />
          <link rel="preconnect" href="https://ssl.google-analytics.com" />
          <link rel="preconnect" href="https://www.googletagmanager.com" />
          <link
            rel="stylesheet"
            href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.2/css/all.min.css"
          />
        </Head>

        <AuthContext.Provider
          value={{
            user,
            signedIn,
            setAuth: this.setAuth,
            hasCookiesAccepted,
            acceptCookie: this.acceptCookie
          }}
        >
          <Suspense fallback={<></>}>
            <Component {...pageProps} err={err as any} />
          </Suspense>
        </AuthContext.Provider>
      </>
    );
  }
}

export default withRouter(MyApp);
